import type {
  Plugin,
  EntityPluginContext,
  EntityTypeConfig,
  JobHandler,
  Template,
  DataSource,
  CreateExecutionContext,
  CreateInput,
  CreateInterceptionResult,
} from "@brains/plugins";
import {
  EntityPlugin,
  SYSTEM_CHANNELS,
  parseMarkdownWithFrontmatter,
  generateMarkdownWithFrontmatter,
} from "@brains/plugins";
import { AtprotoProjectionRegistry } from "@brains/atproto-contracts";
import { getErrorMessage } from "@brains/utils/error";
import { z } from "@brains/utils/zod";
import { paginationInfoSchema } from "@brains/plugins";
import {
  PUBLISH_CHANNELS,
  type PublishProvider,
  type PublishResult,
} from "@brains/contracts";
import { PROJECT_CHANNELS } from "./project-channels";
import { createTemplate } from "@brains/templates";
import { fetchStyleGuide, formatVoiceGuidance } from "@brains/contracts";
import {
  projectSchema,
  projectFrontmatterSchema,
  type Project,
} from "./schemas/project";
import {
  projectAdapter,
  type ProjectAdapter,
} from "./adapters/project-adapter";
import type { PortfolioConfig, PortfolioConfigInput } from "./config";
import { portfolioConfigSchema } from "./config";
import {
  ProjectListTemplate,
  type ProjectListProps,
} from "./templates/project-list";
import {
  ProjectDetailTemplate,
  type ProjectDetailProps,
} from "./templates/project-detail";
import { projectViewSchema } from "./templates/project-view";
import { projectGenerationTemplate } from "./templates/generation-template";
import {
  buildProjectGenerationPrompt,
  generatedProjectContentSchema,
  ProjectGenerationJobHandler,
} from "./handlers/generation-handler";
import { ProjectDataSource } from "./datasources/project-datasource";
import { createProjectAtprotoProjection } from "./atproto-projection";
import { createProjectPrintableProvider } from "./attachments/printable-provider";
import { PROJECT_PRINTABLE_ATTACHMENT_TYPE } from "./attachments/printable-template";
import { createProjectOgImageProvider } from "./attachments/og-image-provider";
import { PROJECT_OG_IMAGE_ATTACHMENT_TYPE } from "./attachments/og-image-template";
import packageJson from "../package.json";

const enrichedProjectViewSchema = projectViewSchema;

const projectListSchema = z.object({
  projects: z.array(enrichedProjectViewSchema),
  pageTitle: z.string().nullable().default(null),
  pagination: paginationInfoSchema.nullable(),
  baseUrl: z.string().nullable().default(null),
});

const generateProjectEvalInputSchema = z.object({
  prompt: z.string(),
  year: z.number(),
});

type GenerateProjectEvalInput = z.output<typeof generateProjectEvalInputSchema>;

function extractProjectYear(
  title: string | undefined,
  prompt: string | undefined,
): number | null {
  for (const value of [title, prompt]) {
    const match = value?.match(/\b(19\d{2}|20\d{2})\b/);
    if (match?.[1]) return Number(match[1]);
  }
  return null;
}

export class PortfolioPlugin extends EntityPlugin<
  Project,
  PortfolioConfig,
  PortfolioConfigInput
> {
  readonly entityType = "project" as const;
  readonly schema: typeof projectSchema = projectSchema;
  readonly adapter: ProjectAdapter = projectAdapter;
  private unregisterAtprotoProjection: (() => void) | undefined;
  private unregisterPrintableAttachmentProvider: (() => void) | undefined;
  private unregisterOgImageAttachmentProvider: (() => void) | undefined;

  constructor(config: PortfolioConfigInput = {}) {
    super("portfolio", packageJson, config, portfolioConfigSchema);
  }

  protected override getEntityTypeConfig(): EntityTypeConfig | undefined {
    return { projectionSourceRole: "secondary" };
  }

  protected override async interceptCreate(
    input: CreateInput,
    executionContext: CreateExecutionContext,
    context: EntityPluginContext,
  ): Promise<CreateInterceptionResult> {
    if (!input.prompt || input.content) {
      return { kind: "continue", input };
    }

    const year = extractProjectYear(input.title, input.prompt);
    if (!year) {
      return { kind: "continue", input };
    }

    const jobId = await context.jobs.enqueue({
      type: PROJECT_CHANNELS.generation,
      data: {
        prompt: input.prompt,
        ...(input.title ? { title: input.title } : {}),
        year,
      },
      toolContext: executionContext,
      options: {
        source: this.id,
        metadata: { operationType: "content_operations" },
      },
    });

    return {
      kind: "handled",
      result: { success: true, data: { status: "generating", jobId } },
    };
  }

  protected override createGenerationHandler(
    context: EntityPluginContext,
  ): JobHandler | null {
    return new ProjectGenerationJobHandler(
      this.logger.child("ProjectGenerationJobHandler"),
      context,
    );
  }

  protected override getTemplates(): Record<string, Template> {
    return {
      "project-list": createTemplate<
        z.output<typeof projectListSchema>,
        ProjectListProps
      >({
        name: "project-list",
        description: "Portfolio project list page template",
        schema: projectListSchema,
        dataSourceId: "portfolio:entities",
        requiredPermission: "public",
        layout: { component: ProjectListTemplate },
      }),
      "project-detail": createTemplate<
        {
          project: z.output<typeof enrichedProjectViewSchema>;
          prevProject: z.output<typeof enrichedProjectViewSchema> | null;
          nextProject: z.output<typeof enrichedProjectViewSchema> | null;
        },
        ProjectDetailProps
      >({
        name: "project-detail",
        description: "Individual project case study template",
        schema: z.object({
          project: enrichedProjectViewSchema,
          prevProject: enrichedProjectViewSchema.nullable(),
          nextProject: enrichedProjectViewSchema.nullable(),
        }),
        dataSourceId: "portfolio:entities",
        requiredPermission: "public",
        layout: { component: ProjectDetailTemplate },
      }),
      generation: projectGenerationTemplate,
    };
  }

  protected override getDataSources(): DataSource[] {
    return [new ProjectDataSource(this.logger.child("ProjectDataSource"))];
  }

  protected override async onRegister(
    context: EntityPluginContext,
  ): Promise<void> {
    this.registerEvalHandlers(context);
    this.unregisterPrintableAttachmentProvider = context.attachments.register(
      "project",
      PROJECT_PRINTABLE_ATTACHMENT_TYPE,
      createProjectPrintableProvider(context),
    );
    this.unregisterOgImageAttachmentProvider = context.attachments.register(
      "project",
      PROJECT_OG_IMAGE_ATTACHMENT_TYPE,
      createProjectOgImageProvider(context),
    );
    this.deferPublishRegistration(context);
    this.subscribeToPublishExecute(context);
    this.unregisterAtprotoProjection =
      AtprotoProjectionRegistry.getInstance().register(
        createProjectAtprotoProjection(),
      );
  }

  protected override async onShutdown(): Promise<void> {
    this.unregisterPrintableAttachmentProvider?.();
    this.unregisterPrintableAttachmentProvider = undefined;
    this.unregisterOgImageAttachmentProvider?.();
    this.unregisterOgImageAttachmentProvider = undefined;
    this.unregisterAtprotoProjection?.();
    this.unregisterAtprotoProjection = undefined;
  }

  private registerEvalHandlers(context: EntityPluginContext): void {
    context.eval.registerHandler("generateProject", async (input: unknown) => {
      const parsed: GenerateProjectEvalInput =
        generateProjectEvalInputSchema.parse(input);
      const voiceGuidance = formatVoiceGuidance(
        await fetchStyleGuide(context.entityService),
      );
      return context.ai.generate(
        {
          prompt: buildProjectGenerationPrompt(parsed),
          templateName: "portfolio:generation",
          representedIdentity: "anchor",
          ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
        },
        generatedProjectContentSchema,
      );
    });
  }

  private deferPublishRegistration(context: EntityPluginContext): void {
    const provider: PublishProvider = {
      name: "internal",
      publish: async (): Promise<PublishResult> => ({ id: "internal" }),
    };

    context.messaging.subscribe(SYSTEM_CHANNELS.pluginsRegistered, async () => {
      await context.messaging.send({
        type: PUBLISH_CHANNELS.register,
        payload: {
          entityType: "project",
          provider,
          config: { executionMode: "provider" },
        },
      });
      return { success: true };
    });
  }

  private subscribeToPublishExecute(context: EntityPluginContext): void {
    context.messaging.subscribe<
      { entityType: string; entityId: string },
      { success: boolean }
    >(PUBLISH_CHANNELS.execute, async (msg) => {
      const { entityType, entityId } = msg.payload;
      if (entityType !== "project") return { success: true };

      try {
        const project = await context.entityService.getEntity(
          {
            entityType: "project",
            id: entityId,
          },
          projectSchema,
        );
        if (!project) {
          await context.messaging.send({
            type: PUBLISH_CHANNELS.reportFailure,
            payload: {
              entityType,
              entityId,
              error: `Project not found: ${entityId}`,
            },
          });
          return { success: true };
        }
        if (project.metadata.status === "published") return { success: true };

        const parsed = parseMarkdownWithFrontmatter(
          project.content,
          projectFrontmatterSchema,
        );
        const publishedAt = new Date().toISOString();
        const updatedContent = generateMarkdownWithFrontmatter(parsed.content, {
          ...parsed.metadata,
          status: "published" as const,
          publishedAt,
        });

        await context.entityService.updateEntity({
          entity: {
            ...project,
            content: updatedContent,
            metadata: { ...project.metadata, status: "published", publishedAt },
          },
        });

        await context.messaging.send({
          type: PUBLISH_CHANNELS.reportSuccess,
          payload: {
            entityType,
            entityId,
            publishedAt,
          },
        });
      } catch (error) {
        await context.messaging.send({
          type: PUBLISH_CHANNELS.reportFailure,
          payload: {
            entityType,
            entityId,
            error: getErrorMessage(error),
          },
        });
      }
      return { success: true };
    });
  }
}

export function portfolioPlugin(config: PortfolioConfigInput = {}): Plugin {
  return new PortfolioPlugin(config);
}
